import { Component, computed, inject, OnDestroy, Signal } from "@angular/core";
import { KartleggingSide } from "./types/KartleggingSide";
import { CategoryIntro } from "./components/category-intro/category-intro";
import { Question } from "./components/question/question";
import { KartleggingService } from "../../services/kartlegging/KartleggingService";
import { SporsmalsType } from "./types/SporsmalsType";
import { Router } from "@angular/router";
import { FeltAlternativ, FormRoot } from "@husbanken/designsystem-angular-forms";
import { KartleggingHeader } from "./components/kartlegging-header/kartlegging-header";

@Component({
  selector: "bko-kartlegging",
  imports: [CategoryIntro, Question, KartleggingHeader],
  templateUrl: "./kartlegging.html",
  styleUrl: "./kartlegging.scss",
})
export class Kartlegging implements OnDestroy {
  router: Router = inject(Router);
  kartleggingService: KartleggingService = inject(KartleggingService);

  // The route-scoped KartleggingService instance is never destroyed by Angular,
  // so drive its cleanup from the component, which *is* destroyed on navigation.
  ngOnDestroy(): void {
    this.kartleggingService.cleanup();
  }

  questionText: Signal<string> = computed(() => this.kartleggingService.currentQuestion().sporsmalsTekst);
  questionImageUri: Signal<string> = this.kartleggingService.questionImageUri;
  questionImageType: Signal<string> = this.kartleggingService.questionImageType;
  questionType: Signal<SporsmalsType> = computed(() => this.kartleggingService.currentQuestion().sporsmalsType);
  helpText: Signal<string> = computed(() => this.kartleggingService.currentQuestion().beskrivelse);
  nextButtonText: Signal<string> = computed(() => {
    if (this.kartleggingService.isFinalQuestion()) {
      return "Fullfør kartleggingen";
    }
    return "Neste";
  });
  currentCategoryNumber: Signal<number> = computed(() => this.kartleggingService.currentCategoryNumber());
  totalNumberOfCategories: Signal<number> = this.kartleggingService.totalNumberOfCategories;
  categoryTitle: Signal<string> = this.kartleggingService.categoryTitle;
  categoryDescription: Signal<string> = this.kartleggingService.categoryDescription;
  categoryImageUri: Signal<string> = this.kartleggingService.categoryImageUri;
  categoryImageType: Signal<string> = this.kartleggingService.categoryImageType;
  categoryIconUri: Signal<string> = this.kartleggingService.categoryIconUri;
  categoryIconType: Signal<string> = this.kartleggingService.categoryIconType;
  currentQuestionNumber: Signal<number> = this.kartleggingService.currentQuestionNumber;
  totalNumberOfQuestionsInCategory: Signal<number> = this.kartleggingService.totalNumberOfQuestionsInCategory;
  showPreviousButton: Signal<boolean> = this.kartleggingService.showPreviousButton;

  currentAlternatives: Signal<FeltAlternativ[]> = this.kartleggingService.currentAlternatives;
  currentForm: Signal<FormRoot> = this.kartleggingService.currentForm;

  onPrevious(): void {
    this.kartleggingService.previousQuestion();
  }

  onNext(): void {
    this.currentForm().markAsSubmitted();
    if (this.kartleggingService.isFinalQuestion()) {
      this.kartleggingService.submitKartlegging().subscribe({
        next: id => {
          this.router.navigate(["/oppsummering", id]);
        },
        error: err => {
          console.error("Kunne ikke lagre besvarelsen", err);
        },
      });
      return;
    }

    this.kartleggingService.nextQuestion();
  }

  onBeginCategory(): void {
    this.kartleggingService.beginNextCategory();
  }

  currentPage: Signal<KartleggingSide> = computed(() => {
    if (this.kartleggingService.shouldShowCategoryIntroduction()) {
      return KartleggingSide.Kategori;
    } else {
      return KartleggingSide.Sporsmal;
    }
  });
  protected readonly KartleggingSide = KartleggingSide;
}
